import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { useMutation } from "@apollo/client";

import { components } from "../../components";
import { useQueryProfile } from "../../graphql/apollo/useQuery";
import AuthLayout from "../../services/auth/jwt/withAuth";
import { CameraIcon } from "../../constants/icons";
import AppUploadSingle from "../../components/apps/AppUpload/UploadSingle";
import { AppState, dispatch, useSelector } from "../../store/store";
import { profileActions } from "../../store/slices/profileSlice";
import AppDialog from "../../components/apps/AppDialog";
import { UPLOAD_PROFILE_IMAGE } from "../../graphql/mutations/profile";
import { showErrorToast } from "../../components/apps/AppError";
import { simpleDate } from "../../utils/dateUtils";
import { getCountryName } from "../../utils/country";

export const PersonalScreen: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { myProfile, refetch } = useQueryProfile();

  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    };

    scrollToTop();
  }, [pathname]);
  const { uploadProfile } = useSelector<AppState["profile"]>(
    ({ profile }) => profile,
  );
  const setUploadedFile = (uri: File) =>
    dispatch(profileActions.setUploadProfileFile({ file: uri }));
  const setPreview = (uri: File) =>
    dispatch(profileActions.setUploadProfilePreview({ preview: uri }));
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const handleClose = () => setShowUpload(!showUpload);

  const [uploadProfileImage, { loading }] = useMutation(UPLOAD_PROFILE_IMAGE, {
    variables: { imageFile: uploadProfile.file },
    onCompleted: (data) => {
      if (data.uploadProfile) {
        refetch();
        handleClose();
        dispatch(profileActions.resetUploadProfile());
      }
    },
    onError: (err) => {
      showErrorToast(err.message);
    },
  });

  const handleSubmit = () => {
    uploadProfileImage();
  };

  const renderGender = (gender?: string) => {
    switch (gender) {
      case "male":
        return t("male");
      case "female":
        return t("female");
      default:
        return "";
    }
  };

  return (
    <AuthLayout>
      <AppDialog
        open={showUpload}
        onClose={handleClose}
        fullHeight
        fullScreen
        showDivider
      >
        <div
          style={{
            paddingBottom: 200,
            overflowY: "scroll",
            overflowX: "clip",
            height: "100vh",
            width: "100wv",
          }}
        >
          <Typography textAlign="center" sx={{ mb: 1 }}>
            {t("yourProfile")}
          </Typography>
          <Typography textAlign="center" variant="body2" sx={{ mb: 2 }}>
            {t("notAllow")}
          </Typography>
          <div className="not-allow_card">
            <p>- {t("sexually")}</p>
            <p>- {t("incite")}</p>
            <p>- {t("underAge")}</p>
            <p>- {t("copyRight")}</p>
            <p>- {t("imageLarger")}</p>
          </div>
          <Typography textAlign="center" variant="body2" sx={{ mt: 2 }}>
            {t("faceMustBeClearly")}
          </Typography>
          <Typography variant="body2" textAlign="center">
            {t("allPhotos")}
          </Typography>
          <AppUploadSingle
            setUploadedFile={setUploadedFile}
            uploadFile={uploadProfile?.file}
            setPreview={setPreview}
            preview={uploadProfile.preview}
            uploadText={t("selectAPhoto")}
            handleSubmit={handleSubmit}
            uploading={loading}
          />
        </div>
      </AppDialog>
      <components.Header
        themeColor="light"
        title={t("personalData")}
        goBack={true}
      />
      <main style={{ paddingBottom: 0, paddingTop: 50 }} className="personal">
        <div className="personal_date">
          <p>
            {t("registerDate")}: {simpleDate(myProfile?.createdAt)}
          </p>
          <p>
            {t("idProfile")}: {myProfile?.memberId}
          </p>
        </div>
        <div className="personal_upload">
          {myProfile?.avatar ? (
            <img src={myProfile?.avatar?.url} alt="avatar" />
          ) : (
            <div className="personal_upload-avatar">
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CameraIcon size={50} color="grey" />
              </Box>
            </div>
          )}
          <Typography sx={{ mt: 2 }}>{myProfile?.displayName}</Typography>
          {myProfile?.avatar ? (
            <Button
              variant="text"
              color="warning"
              fullWidth
              onClick={() => setShowUpload(true)}
              sx={{ textTransform: "none" }}
            >
              <CameraIcon size={20} style={{ marginRight: "5px" }} />{" "}
              {t("changePhoto")}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={() => setShowUpload(true)}
            >
              {t("uploadPhoto")}
            </Button>
          )}
        </div>
        <Typography variant="h6">{t("contactInformation")}:</Typography>
        <Typography>
          {t("gender")}: {renderGender(myProfile?.gender)}
        </Typography>
        <Typography>
          {t("dateOfBirth")}: {simpleDate(myProfile?.birthDate)}
        </Typography>
        <Typography>
          {t("country")}: {getCountryName(myProfile?.country)}
        </Typography>
        <Typography>
          {t("province")}: {myProfile?.city}
        </Typography>
        <Typography>
          {t("address")}: {myProfile?.address}
        </Typography>
      </main>
    </AuthLayout>
  );
};
